import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Coins,
  Users,
  Search,
  Plus,
  Minus,
  RefreshCw,
  Loader2,
  CreditCard,
  TrendingUp,
  Building2,
  Stethoscope,
  Heart,
  Clock,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const AdminCredits = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState({
    totalCredits: 0,
    totalUsers: 0,
    totalTransactions: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load users with credit balances
      const usersRes = await api.get('/admin/users');
      setUsers(usersRes.data.users || []);
      
      // Load recent transactions
      try {
        const txRes = await api.get('/credits/admin/transactions');
        setTransactions(txRes.data.transactions || []);
      } catch (e) {
        console.log('No admin transactions endpoint');
      }

      // Calculate stats
      const usersWithCredits = (usersRes.data.users || []).filter(u => u.credits?.balance > 0);
      const totalCredits = usersWithCredits.reduce((sum, u) => sum + (u.credits?.balance || 0), 0);
      
      setStats({
        totalCredits,
        totalUsers: usersWithCredits.length,
        totalTransactions: transactions.length
      });
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCreditModal = (user, type = 'add') => {
    setSelectedUser({ ...user, actionType: type });
    setCreditAmount('');
    setCreditReason('');
    setShowCreditModal(true);
  };

  const handleCreditAction = async () => {
    if (!creditAmount || parseInt(creditAmount) <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }

    setProcessing(true);
    try {
      const amount = selectedUser.actionType === 'add' 
        ? parseInt(creditAmount) 
        : -parseInt(creditAmount);
      
      await api.post('/credits/admin/adjust', {
        user_id: selectedUser.id,
        amount,
        reason: creditReason || (amount > 0 ? 'Attribution manuelle par admin' : 'Retrait manuel par admin')
      });

      toast.success(
        amount > 0 
          ? `${creditAmount} crédits ajoutés à ${selectedUser.full_name}` 
          : `${Math.abs(amount)} crédits retirés de ${selectedUser.full_name}`
      );
      
      setShowCreditModal(false);
      loadData();
    } catch (error) {
      console.error('Credit action error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    } finally {
      setProcessing(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleIcon = (role) => {
    switch (role) {
      case 'facility': return <Building2 className="w-4 h-4 text-indigo-600" />;
      case 'professional': return <Stethoscope className="w-4 h-4 text-red-500" />;
      case 'patient': return <Heart className="w-4 h-4 text-blue-500" />;
      default: return <Users className="w-4 h-4 text-slate-500" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'facility': return 'bg-indigo-100 text-indigo-700';
      case 'professional': return 'bg-red-100 text-red-700';
      case 'patient': return 'bg-blue-100 text-blue-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'agent': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="admin-credits-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/admin/dashboard')}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour au dashboard
      </Button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Coins className="w-8 h-8 text-amber-500" />
              Gestion des Crédits
            </h1>
            <p className="text-slate-500 mt-1">Attribuez et gérez les crédits des utilisateurs</p>
          </div>
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Coins className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalCredits.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Crédits en circulation</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalUsers}</p>
                  <p className="text-xs text-slate-500">Utilisateurs avec crédits</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{transactions.length}</p>
                  <p className="text-xs text-slate-500">Transactions récentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Utilisateurs</CardTitle>
                  <CardDescription>Gérez les crédits de chaque utilisateur</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          {getRoleIcon(user.role)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.full_name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                        <Badge className={`ml-2 ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-slate-900">{user.credits?.balance || 0}</p>
                          <p className="text-xs text-slate-500">crédits</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCreditModal(user, 'add')}
                            className="h-8 w-8 p-0"
                            title="Ajouter des crédits"
                            aria-label={`Ajouter des crédits à ${user.full_name}`}
                            data-testid={`add-credits-${user.id}`}
                          >
                            <Plus className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCreditModal(user, 'remove')}
                            className="h-8 w-8 p-0"
                            title="Retirer des crédits"
                            aria-label={`Retirer des crédits de ${user.full_name}`}
                            data-testid={`remove-credits-${user.id}`}
                            disabled={!user.credits?.balance}
                          >
                            <Minus className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>Aucun utilisateur trouvé</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>Transactions récentes</CardTitle>
              <CardDescription>Historique global des transactions de crédits</CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Aucune transaction</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {transactions.map((tx) => (
                      <div 
                        key={tx.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.amount > 0 ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            <CreditCard className={`w-5 h-5 ${
                              tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-slate-500">
                              {tx.user_email || tx.user_id}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount}
                          </p>
                          <p className="text-xs text-slate-500">
                            {format(new Date(tx.created_at), 'dd/MM/yy HH:mm', { locale: fr })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Credit Modal */}
      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedUser?.actionType === 'add' ? (
                <><Plus className="w-5 h-5 text-green-600" /> Ajouter des crédits</>
              ) : (
                <><Minus className="w-5 h-5 text-red-600" /> Retirer des crédits</>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.full_name} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <p className="text-sm text-slate-500">Solde actuel</p>
              <p className="text-3xl font-bold text-slate-900">
                {selectedUser?.credits?.balance || 0} crédits
              </p>
            </div>

            <div>
              <Label>Montant de crédits</Label>
              <Input
                type="number"
                min="1"
                max={selectedUser?.actionType === 'remove' ? (selectedUser?.credits?.balance || 0) : undefined}
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="Ex: 100"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Raison (optionnel)</Label>
              <Input
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder="Ex: Bonus de bienvenue"
                className="mt-1"
              />
            </div>

            {creditAmount && (
              <div className={`p-3 rounded-lg ${
                selectedUser?.actionType === 'add' ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <p className={`text-sm font-medium ${
                  selectedUser?.actionType === 'add' ? 'text-green-700' : 'text-red-700'
                }`}>
                  Nouveau solde: {
                    selectedUser?.actionType === 'add'
                      ? (selectedUser?.credits?.balance || 0) + parseInt(creditAmount || 0)
                      : Math.max(0, (selectedUser?.credits?.balance || 0) - parseInt(creditAmount || 0))
                  } crédits
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCreditModal(false)}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreditAction}
              disabled={processing || !creditAmount || parseInt(creditAmount) <= 0}
              className={`flex-1 ${
                selectedUser?.actionType === 'add' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                selectedUser?.actionType === 'add' ? 'Ajouter' : 'Retirer'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCredits;
